import type { ReactElement } from "react";
import {
  Avatar,
  AvatarFallback,
  Badge,
  StatusPill,
  cn,
  initialsOf,
} from "@playbookai/ui";
import type { Team } from "../../api/teams-api.js";

/**
 * The small pieces every Teams screen draws the same way: a team's tile, a
 * person's initials, the count beside a tab, and the viewer's own status.
 *
 * ONE place for the viewer's status, because it is the fact the consent model
 * exists to keep honest. `viewerRole` and `viewerPendingKind` are separate
 * fields, and a pending person must never read as a member — the list row, the
 * team header and the request queue all render it through `TeamViewerStatus`,
 * so no one of them can drift into showing an invitation as a membership.
 */

/** A team's square tile. Square rather than round, so a team never reads as a
 *  person beside the round person avatars in the same list. */
export function TeamTile({
  name,
  size = "md",
}: {
  name: string;
  size?: "md" | "lg";
}): ReactElement {
  /* Kept: Avatar shape="rounded" is the 8px corner (today 10/14px) and its
     neutral tone paints text-pb-text, not today's secondary; no PR 3 look row
     approves either. */
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center border border-pb-border bg-pb-card-elevated font-semibold text-pb-text-secondary",
        size === "lg"
          ? "h-12 w-12 rounded-xl text-body"
          : "h-8 w-8 rounded-lg text-dense-1",
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

/**
 * A person's round initials, in the per-person gradient every avatar in the
 * product uses (`avatarToneFor`, PB-5134 d36). Seeded by the user id, so the
 * same person is the same colour on the roster, in the request queues, in the
 * people picker and in the profile menu. Decorative: the name beside it is the
 * label.
 */
export function PersonInitials({
  name,
  seed,
}: {
  name: string | null;
  /** The user id. A name is only the fallback seed — two people share names. */
  seed: string;
}): ReactElement {
  /* The person's seeded Avatar (OK-65): Avatar paints the gradient and
     sets the semibold initials at its sm step. */
  return (
    <Avatar size="sm" seed={seed} className="shrink-0" aria-hidden="true">
      <AvatarFallback>{initialsOf(name ?? "")}</AvatarFallback>
    </Avatar>
  );
}

/**
 * The count beside a tab label. `attention` is for a count the reader has to
 * act on (requests waiting for them); every other count stays neutral, so the
 * accent keeps meaning "this needs you".
 */
export function TabCount({
  value,
  attention = false,
}: {
  value: string | number;
  attention?: boolean;
}): ReactElement {
  /* Kept: Badge adds a 1px border and has no accent paint, and its pill
     sizes are not this 18px caption count; no PR 3 look row approves it. */
  return (
    <span
      className={cn(
        "inline-flex h-4.5 min-w-5 items-center justify-center rounded-full px-1.5 text-caption font-semibold tabular-nums",
        attention
          ? "bg-pb-accent-soft text-pb-accent-text"
          : "bg-pb-card-elevated text-pb-text-muted",
      )}
    >
      {value}
    </span>
  );
}

/** A tab label with its count, or the bare label while the count is unknown —
 *  a "0" before the first page lands would be a false statement. */
export function tabLabel(
  label: string,
  count: string | number | undefined,
  attention = false,
): ReactElement {
  return (
    <span className="inline-flex items-center gap-2">
      {label}
      {/* A real space, so the accessible name is "My teams 3", not "My
          teams3". A flex container does not render a whitespace-only run, so
          the gap alone sets the visual spacing. */}
      {count === undefined ? null : (
        <>
          {" "}
          <TabCount value={count} attention={attention} />
        </>
      )}
    </span>
  );
}

/**
 * The VIEWER's own relation to a team. Pending is never "member": an invitation
 * reads Invited and the viewer's own request reads Requested, and neither is
 * derived from `memberCount`.
 */
export function TeamViewerStatus({
  team,
  quietWhenNone = false,
}: {
  team: Pick<Team, "viewerRole" | "viewerPendingKind" | "archivedAt">;
  /** Say nothing (rather than "Not on this team") when the viewer has no
   *  relation — the team header, where the missing badge already says it. */
  quietWhenNone?: boolean;
}): ReactElement | null {
  const archived = team.archivedAt !== null;
  const parts: ReactElement[] = [];
  if (team.viewerRole === "owner") parts.push(<Badge key="owner">Owner</Badge>);
  if (team.viewerRole === "member")
    parts.push(<Badge key="member">Member</Badge>);
  if (team.viewerPendingKind === "invitation")
    parts.push(
      <StatusPill key="invited" tone="info">
        Invited
      </StatusPill>,
    );
  if (team.viewerPendingKind === "join_request")
    parts.push(
      <StatusPill key="requested" tone="info">
        Requested
      </StatusPill>,
    );
  if (archived)
    parts.push(
      <StatusPill key="archived" tone="watch">
        Archived
      </StatusPill>,
    );
  if (parts.length === 0)
    return quietWhenNone ? null : (
      <span className="text-body-small text-pb-text-muted">
        Not on this team
      </span>
    );
  return <span className="flex flex-wrap items-center gap-1.5">{parts}</span>;
}
